import { supabase } from "../supabaseClient";
import { logger } from "../utils/logger";

const DEFAULT_SETTINGS = { font_scale: 1.0, is_parent: false };

export async function getUserSettings(userId) {
  const { data, error } = await supabase
    .from("users")
    .select("font_scale, is_parent")
    .eq("user_id", userId)
    .single();

  if (error) return { ...DEFAULT_SETTINGS };

  return {
    font_scale: typeof data?.font_scale === "number" ? data.font_scale : DEFAULT_SETTINGS.font_scale,
    is_parent: typeof data?.is_parent === "boolean" ? data.is_parent : DEFAULT_SETTINGS.is_parent,
  };
}

export async function updateUserSettings(userId, newSettings) {
  const payload = {};
  if (typeof newSettings?.font_scale === "number") payload.font_scale = newSettings.font_scale;
  if (typeof newSettings?.is_parent === "boolean") payload.is_parent = newSettings.is_parent;

  const { data, error } = await supabase
    .from("users")
    .update(payload)
    .eq("user_id", userId)
    .select("font_scale, is_parent")
    .single();

  return { data, error };
}

export async function getUserProfile(userId) {
  const { data, error } = await supabase
    .from("users")
    .select("font_scale, is_parent, role, primary_group_id")
    .eq("user_id", userId)
    .single();

  if (error) {
    logger.warn('usersService', 'get_profile_failed', { userId, error: error.message });
    return undefined;
  }
  return data;
}

export async function getDisplayName(userId) {
  const { data, error } = await supabase
    .from("users")
    .select("display_name")
    .eq("user_id", userId)
    .single();

  if (error) {
    logger.warn('usersService', 'get_display_name_failed', { userId, error: error.message });
    return null;
  }
  return data?.display_name ?? null;
}

export async function getUserIdByAuthId(authId) {
  const { data, error } = await supabase
    .from("users")
    .select("user_id")
    .eq("auth_id", authId)
    .maybeSingle();

  if (error) {
    logger.warn('usersService', 'get_user_by_auth_id_failed', { error: error.message });
    return null;
  }
  return data?.user_id ?? null;
}

export async function checkUsernameAvailable(userId) {
  const { data, error } = await supabase.rpc("username_available", { p_user_id: userId });

  if (error) {
    logger.error('usersService', 'username_check_failed', { userId, error: error.message });
    throw error;
  }
  return data;
}

export async function getProfileByUserId(userId) {
  const { data, error } = await supabase
    .from("users")
    .select("user_id, auth_id, display_name, primary_group_id")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    logger.error('usersService', 'get_profile_by_id_failed', { userId, error: error.message });
    throw error;
  }
  return data || null;
}

export async function linkAdminBootstrap({ userId, authId }) {
  const { error } = await supabase
    .from("users")
    .update({ auth_id: authId })
    .eq("user_id", userId)
    .is("auth_id", null);

  if (error) {
    logger.warn('usersService', 'admin_bootstrap_link_failed', { userId, error: error.message });
  }
}

export async function linkAuthId({ userId, authId }) {
  const { error } = await supabase.from("users").update({ auth_id: authId }).eq("user_id", userId);

  if (error) {
    logger.error('usersService', 'link_auth_id_failed', { userId, error: error.message });
    throw error;
  }
}

export async function createUserProfile({ userId, displayName, authId }) {
  const { error } = await supabase.from("users").insert({
    user_id: userId,
    display_name: displayName,
    auth_id: authId,
  });

  if (error) {
    logger.error('usersService', 'profile_create_failed', { userId, error: error.message });
    throw error;
  }
  logger.info('usersService', 'profile_created', { userId });
}

export async function acceptInvite({ userId, isParent }) {
  const { error } = await supabase
    .from("users")
    .update({ is_parent: isParent })
    .eq("user_id", userId);

  if (error) {
    logger.error('usersService', 'accept_invite_failed', { userId, error: error.message });
    throw error;
  }
}

export async function setIsParent({ userId, isParent }) {
  const { error } = await supabase.from("users").update({ is_parent: isParent }).eq("user_id", userId);

  if (error) {
    logger.error('usersService', 'is_parent_set_failed', { userId, error: error.message });
    throw error;
  }
}

export async function setPrimaryGroup({ userId, groupId }) {
  const { error } = await supabase.from("users").update({ primary_group_id: groupId }).eq("user_id", userId);

  if (error) {
    logger.error('usersService', 'primary_group_change_failed', { userId, groupId, error: error.message });
    throw error;
  }
}

export async function listUsers() {
  const { data, error } = await supabase
    .from("users")
    .select("user_id, display_name, role, primary_group_id")
    .order("user_id", { ascending: true });

  if (error) {
    logger.error('usersService', 'list_users_failed', { error: error.message });
    throw error;
  }
  return data || [];
}

export async function checkIsSoleAdmin() {
  const { count, error } = await supabase
    .from("users")
    .select("*", { count: "exact", head: true })
    .eq("role", "admin");

  if (error) {
    logger.error('usersService', 'sole_admin_check_failed', { error: error.message });
    throw error;
  }
  return (count ?? 0) <= 1;
}

export async function updateRole({ userId, currentRole, newRole }) {
  if (currentRole === "admin" && newRole !== "admin" && (await checkIsSoleAdmin())) {
    throw new Error("LAST_ADMIN");
  }

  const { error } = await supabase.from("users").update({ role: newRole }).eq("user_id", userId);

  if (error) {
    logger.error('usersService', 'role_change_failed', { targetUser: userId, error: error.message });
    throw error;
  }
  logger.info('usersService', 'role_changed', { targetUser: userId, newRole });
}

export async function renameUser({ oldId, newId }) {
  const { error } = await supabase.rpc("rename_user", { old_user: oldId, new_user: newId });

  if (error) {
    logger.error('usersService', 'user_rename_failed', { oldId, newId, error: error.message });
    throw error;
  }
  logger.info('usersService', 'user_renamed', { oldId, newId });
}

export async function deleteUser({ userId, leaderReassignments }) {
  const body = { target_user_id: userId };
  if (Array.isArray(leaderReassignments) && leaderReassignments.length > 0) {
    body.leader_reassignments = leaderReassignments.map((r) => ({
      group_id: r.group_id, new_leader_id: r.new_leader_id,
    }));
  }
  const { error } = await supabase.functions.invoke("delete-account", { body });

  if (error) {
    logger.error('usersService', 'user_delete_failed', { targetUser: userId, error: error.message });
    throw new Error(error.message || "Failed to delete user.");
  }
  logger.info('usersService', 'user_deleted', { targetUser: userId });
}

export async function getSortOrder(userId) {
  const { data, error } = await supabase
    .from("users")
    .select("claimed_sort_order")
    .eq("user_id", userId)
    .single();

  if (error) {
    logger.error('usersService', 'get_sort_order_failed', { userId, error: error.message });
    throw error;
  }
  return data?.claimed_sort_order || "desc";
}

export async function setSortOrder({ userId, order }) {
  const { error } = await supabase.from("users").update({ claimed_sort_order: order }).eq("user_id", userId);

  if (error) {
    logger.error('usersService', 'set_sort_order_failed', { userId, order, error: error.message });
    throw error;
  }
  logger.info('usersService', 'sort_order_changed', { userId, order });
}

// Guarded: never clobber a primary group the user already has. Called from
// groupsService.addMember (join/create request approval, admin Add Member) — see
// docs/superpowers/specs/2026-08-04-users-migration-design.md's Cross-Service Boundary
// section for why groupsService is allowed to import this one function.
export async function setPrimaryGroupIfUnset({ userId, groupId }) {
  const { error } = await supabase
    .from("users")
    .update({ primary_group_id: groupId })
    .eq("user_id", userId)
    .is("primary_group_id", null);

  if (error) {
    // Membership already landed in the caller — don't fail the join over this.
    logger.error('usersService', 'primary_group_set_failed', { userId, groupId, error: error.message });
  }
}

// Called from groupsService.removeMember/deleteGroup and useGroupRequests's leave-approval
// branch — same cross-service exception as setPrimaryGroupIfUnset above.
export async function clearPrimaryGroupIfMatches({ userId, groupId }) {
  const { error } = await supabase
    .from("users")
    .update({ primary_group_id: null })
    .eq("user_id", userId)
    .eq("primary_group_id", groupId);

  if (error) {
    logger.error('usersService', 'primary_group_clear_failed', { userId, groupId, error: error.message });
    throw error;
  }
}
